import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../Dashboard/components/CardUI";
import apiService from "../../../../core/services/apiService";
import {
  ResponsiveContainer,
  XAxis,
  Tooltip,
  Legend,
  Line,
  LineChart,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import {
  Package,
  ArrowDownCircle,
  ArrowUpCircle,
  AlertTriangle,
  Pencil,
} from "lucide-react";
import Reloader from "../../../../core/components/utils/reloader";
import TransactionTable from "./TransactionTable";
import StatCard from "./StatCard";
import { adminOnlyRoles } from "../../../../core/constants/rolePresets";
import InventoryModal from "../Modals/InventoryModal";
import InventoryDetailsSkeleton from "../../../../core/components/skeleton/InventoryDetailsSkeleton";

export default function InventoryDetailsPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userInfo);
  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [expiryInfo, setExpiryInfo] = useState({
    status: "none",
    message: "No expiry date set",
  });
  const [search, setSearch] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1–12
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showModal, setShowModal] = useState(false);

  const canEdit = currentUser && adminOnlyRoles.includes(currentUser.role);

  useEffect(() => {
    loadInventory();
  }, [id]);

  // --- Effect to compute expiry info only when item is available
  useEffect(() => {
    if (!item) return;

    // Some items have no expiry_date, handle gracefully
    if (!item.expiryDate) {
      setExpiryInfo({ status: "none", message: "No expiry date set" });
      return;
    }

    // Otherwise, compute expiry status
    setExpiryInfo(getExpiryStatus(item.expiryDate));
  }, [item]);

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: "none", message: "No expiry date set" };

    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0)
      return {
        status: "expired",
        message: `Expired ${Math.abs(diffDays)} days ago`,
      };
    if (diffDays <= 90)
      return { status: "warning", message: `Expiring in ${diffDays} days` };

    return { status: "ok", message: `Expires in ${diffDays} days` };
  };

  const loadInventory = async () => {
    try {
      const [itemRes, histRes] = await Promise.all([
        apiService.get(dispatch, `inventory/${id}`),
        apiService.get(dispatch, `inventoryLogs`, { item: id }),
      ]);

      setItem(itemRes);
      setHistory(histRes);
      computeStats(histRes);
    } catch (err) {
      console.error(err);
    }
  };

  const computeStats = (transactions) => {
    if (!transactions) return;

    // --- All-time totals ---
    const totalStockIn = transactions
      .filter((t) => t.type === "Stock in")
      .reduce((sum, t) => sum + t.quantity, 0);

    const totalStockOut = transactions
      .filter((t) => t.type === "Stock out")
      .reduce((sum, t) => sum + t.quantity, 0);

    const current = totalStockIn - totalStockOut;

    // --- Monthly totals ---
    const now = new Date();
    const thisMonth = now.getMonth(); // 0-11
    const thisYear = now.getFullYear();

    const lastMonthDate = new Date(thisYear, thisMonth - 1);
    const lastMonth = lastMonthDate.getMonth();
    const lastMonthYear = lastMonthDate.getFullYear();

    const monthlyStockIn = transactions
      .filter(
        (t) =>
          t.type === "Stock in" &&
          new Date(t.created_on).getMonth() === thisMonth &&
          new Date(t.created_on).getFullYear() === thisYear
      )
      .reduce((sum, t) => sum + t.quantity, 0);

    const lastMonthStockIn = transactions
      .filter(
        (t) =>
          t.type === "Stock in" &&
          new Date(t.created_on).getMonth() === lastMonth &&
          new Date(t.created_on).getFullYear() === lastMonthYear
      )
      .reduce((sum, t) => sum + t.quantity, 0);

    const monthlyStockOut = transactions
      .filter(
        (t) =>
          t.type === "Stock out" &&
          new Date(t.created_on).getMonth() === thisMonth &&
          new Date(t.created_on).getFullYear() === thisYear
      )
      .reduce((sum, t) => sum + t.quantity, 0);

    const lastMonthStockOut = transactions
      .filter(
        (t) =>
          t.type === "Stock out" &&
          new Date(t.created_on).getMonth() === lastMonth &&
          new Date(t.created_on).getFullYear() === lastMonthYear
      )
      .reduce((sum, t) => sum + t.quantity, 0);

    // --- Calculate trend: up if this month > last month ---
    const stockInTrend = monthlyStockIn >= lastMonthStockIn ? "up" : "down";
    const stockOutTrend = monthlyStockOut >= lastMonthStockOut ? "up" : "down";

    const safePercentageChange = (current, previous) => {
      if (previous === 0) return current === 0 ? 0 : 100;
      const value = ((current - previous) / previous) * 100;
      return parseFloat(value.toFixed(2)); // keeps only 2 decimal places
    };

    setStats({
      totalStockIn,
      totalStockOut,
      current,
      deltas: {
        stockIn: safePercentageChange(monthlyStockIn, lastMonthStockIn),
        stockInTrend,
        stockOut: safePercentageChange(monthlyStockOut, lastMonthStockOut),
        stockOutTrend,
      },
    });
  };

  const filteredHistory = useMemo(() => {
    return history.filter(
      (t) =>
        t.type.toLowerCase().includes(search.toLowerCase()) ||
        (t.remarks || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [history, search]);

  // --- Helper: format and filter transactions by selected month/year ---
  const formatChartData = (transactions) => {
    const filtered = transactions.filter((t) => {
      if (!t.created_on) return false; // skip invalid
      const d = new Date(t.created_on);
      return (
        d.getMonth() + 1 === Number(selectedMonth) &&
        d.getFullYear() === Number(selectedYear)
      );
    });

    const grouped = {};
    filtered.forEach((t) => {
      const date = new Date(t.created_on).getDate(); // group by day of month
      if (!grouped[date])
        grouped[date] = { day: date, stockIn: 0, stockOut: 0 };
      if (t.type === "Stock in") grouped[date].stockIn += t.quantity;
      if (t.type === "Stock out") grouped[date].stockOut += t.quantity;
    });

    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();

    // fill missing days with zeroes
    return Array.from({ length: daysInMonth }, (_, i) => ({
      day: i + 1,
      stockIn: grouped[i + 1]?.stockIn || 0,
      stockOut: grouped[i + 1]?.stockOut || 0,
    }));
  };

  const chartData = useMemo(
    () => formatChartData(history),
    [history, selectedMonth, selectedYear]
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from({ length: 6 }, (_, i) => 2022 + i); // adjust range as needed

  if (!item) return <InventoryDetailsSkeleton />;

  return (
    <>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Package className="w-6 h-6 text-blue-600" />
              {item.name}
            </h1>
            <p className="text-gray-500 text-sm">
              {item.category} •{" "}
              {item.expiryDate ? (
                <span className="text-gray-700 font-medium">
                  Expires on {new Date(item.expiryDate).toLocaleDateString()}
                </span>
              ) : (
                <span className="text-gray-400 italic">No expiry date</span>
              )}
            </p>
          </div>

          {/* ✅ Show Edit button only for admins */}
          {canEdit && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 text-sm"
            >
              <Pencil size={14} className="mr-1" /> Edit
            </button>
          )}
        </div>

        {expiryInfo.status === "expired" && (
          <div className="flex items-center gap-2 text-red-700 bg-red-100 px-3 py-1 rounded-md text-sm">
            <AlertTriangle className="w-4 h-4" /> {expiryInfo.message}
          </div>
        )}

        {expiryInfo.status === "warning" && (
          <div className="flex items-center gap-2 text-amber-700 bg-amber-100 px-3 py-1 rounded-md text-sm">
            <AlertTriangle className="w-4 h-4" /> {expiryInfo.message}
          </div>
        )}
        {stats.current < item.reorderLevel && (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-100 px-3 py-1 rounded-md text-sm">
            <AlertTriangle className="w-4 h-4" /> Low stock! Consider
            restocking.
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Package}
            title="Current Stock"
            value={item.quantity}
          />
          <StatCard
            icon={AlertTriangle}
            title="Reorder Level"
            value={item?.reorderLevel || 0}
          />
          <StatCard
            icon={ArrowDownCircle}
            title="Stock In (All time)"
            value={stats.totalStockIn}
            delta={`${stats.deltas?.stockIn || 0}% this month`}
            trend={stats.deltas?.stockInTrend}
          />
          <StatCard
            icon={ArrowUpCircle}
            title="Stock Out (All time)"
            value={stats.totalStockOut}
            delta={`${stats.deltas?.stockOut || 0}% this month`}
            trend={stats.deltas?.stockOutTrend}
          />
        </div>

        {/* Chart */}
        <Card className="shadow-sm">
          <CardHeader className="flex flex-wrap justify-between items-center gap-3">
            <CardTitle>Stock Movement Overview</CardTitle>

            {/* Filters: Month + Year */}
            <div className="flex items-center gap-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="border rounded-md px-2 py-1 text-sm focus:ring focus:ring-blue-200 outline-none"
              >
                {months.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="border rounded-md px-2 py-1 text-sm focus:ring focus:ring-blue-200 outline-none"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </CardHeader>

          <CardContent>
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-6">
                No transaction data available.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <XAxis
                    dataKey="day"
                    tickFormatter={(day) => `${day}`}
                    label={{
                      value: `${months[selectedMonth - 1]} ${selectedYear}`,
                      position: "insideBottom",
                      offset: -5,
                    }}
                  />
                  <Tooltip
                    labelFormatter={(day) =>
                      `${months[selectedMonth - 1]} ${day}, ${selectedYear}`
                    }
                  />
                  <Legend
                    wrapperStyle={{
                      paddingTop: 10,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="stockIn"
                    name="Stock In"
                    stroke="#16a34a"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="stockOut"
                    name="Stock Out"
                    stroke="#dc2626"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* History Table */}
        <Card className="shadow-sm">
          <CardHeader className="flex justify-between items-center">
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>

          <CardContent>
            {filteredHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-6">
                No matching transactions found.
              </p>
            ) : (
              <TransactionTable transactions={filteredHistory} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* ✅ Edit Modal */}
      {showModal && (
        <InventoryModal
          onClose={() => setShowModal(false)}
          existingItem={item}
          onUpdated={loadInventory} // callback to reload after save
        />
      )}
    </>
  );
}
